import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEOSchema from '../components/SEOSchema';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProgramFramework from '../components/ProgramFramework';
import { 
  Stethoscope, 
  Shield, 
  CheckCircle, 
  ArrowRight, 
  Users, 
  Heart,
  Activity,
  Award,
  Clock,
  Target,
  Sparkles,
  ChevronDown
} from 'lucide-react';

const DoctorLedFitnessPage = () => {
  const [openFaq, setOpenFaq] = React.useState(null);

  const benefits = [
    {
      icon: <Stethoscope className="w-6 h-6" />,
      title: "Medical Expertise Integration",
      description: "Every fitness plan is designed with medical insight. Your doctor reviews your health history, medications, and conditions to create safe, effective programs."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Safety-First Approach",
      description: "Unlike generic fitness programs, doctor-led coaching ensures your workouts and nutrition plans account for any health limitations or medical requirements."
    },
    {
      icon: <Activity className="w-6 h-6" />,
      title: "Evidence-Based Methods",
      description: "All recommendations are grounded in medical research and clinical experience, not trends or fads. Science guides every decision."
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Personalized Health Goals",
      description: "Your program targets your specific health markers, whether that is blood sugar management, cholesterol improvement, or hormonal balance."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Multidisciplinary Team",
      description: "Access a complete healthcare team including medical doctors, gynecologists, fertility specialists, and certified nutrition experts."
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Qualified Professionals",
      description: "Work with MBBS-qualified doctors and MSc-certified nutritionists who bring clinical expertise to your fitness journey."
    }
  ];

  const conditions = [
    "Diabetes and Pre-diabetes Management",
    "Thyroid Disorders (Hypothyroidism, Hyperthyroidism)",
    "PMOS (previously called PCOS)",
    "Hypertension and Heart Health",
    "Obesity and Metabolic Syndrome",
    "Post-Surgery Recovery",
    "Chronic Fatigue and Low Energy",
    "Digestive Health Issues"
  ];

  const process = [
    {
      step: 1,
      title: "Comprehensive Health Assessment",
      description: "Complete medical history review including current medications, past conditions, family history, and lifestyle factors."
    },
    {
      step: 2,
      title: "Lab Report Analysis",
      description: "Our medical team reviews your blood work, metabolic markers, and any relevant diagnostic reports to understand your baseline health."
    },
    {
      step: 3,
      title: "Customized Program Design",
      description: "Based on your health profile, we create a personalized fitness and nutrition program that addresses your specific needs and goals."
    },
    {
      step: 4,
      title: "Ongoing Medical Monitoring",
      description: "Regular check-ins with medical supervision ensure your progress is safe and your program adjusts as your health improves."
    }
  ];

  const faqs = [
    {
      question: "What makes doctor-led fitness different from regular personal training?",
      answer: "Doctor-led fitness programs integrate medical expertise into every aspect of your health journey. Unlike regular personal training, our approach includes comprehensive health assessments, consideration of medical conditions and medications, and ongoing medical supervision. This ensures your fitness plan is not only effective but also safe for your specific health situation."
    },
    {
      question: "Who should consider a doctor-led nutrition program?",
      answer: "Doctor-led nutrition programs are ideal for anyone with existing health conditions such as diabetes, thyroid disorders, PMOS, hypertension, or obesity. They are also beneficial for individuals on medications, those recovering from surgery, pregnant or postpartum women, and anyone who wants the assurance of medical supervision for their health transformation."
    },
    {
      question: "Can doctor-led fitness help with chronic conditions?",
      answer: "Yes, doctor-led fitness is specifically designed to support individuals with chronic conditions. Our medical team creates programs that work alongside your existing treatments, helping improve health markers while ensuring safety. Many clients see improvements in their blood sugar levels, cholesterol, blood pressure, and hormonal balance through our medically supervised approach."
    },
    {
      question: "How do online doctor-led programs work?",
      answer: "Our 100% online programs include consultations with our medical team via phone and chat, personalized diet and workout plans delivered through our private app, regular check-in calls with your coach, and continuous monitoring of your progress. You receive the same quality of medical supervision as in-person care, with the convenience of training from anywhere."
    },
    {
      question: "What qualifications do your medical team members have?",
      answer: "Our team includes MBBS-qualified doctors with specialized training in nutrition and fitness, MS-qualified gynecologists, reproductive medicine specialists, and MSc and BSc certified nutrition experts with diplomas in sports nutrition. Each team member brings clinical expertise and hands-on experience in their respective fields."
    },
    {
      question: "Is doctor-led fitness more expensive than regular coaching?",
      answer: "While doctor-led programs may have a higher investment than generic fitness coaching, they provide significantly more value through medical expertise, personalized health assessments, and ongoing supervision. The safety and effectiveness of medically supervised programs often lead to better long-term results and can help avoid health complications that might arise from unsuitable generic programs."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Doctor-Led Online Fitness &amp; Nutrition Coaching | Medical Supervision | TCUBE FITNESS</title>
        <meta name="description" content="Transform your health with doctor-led fitness programs. Medical supervision, personalized nutrition, and evidence-based training for diabetes, thyroid, PMOS, and more. 100% online coaching." />
        <meta name="keywords" content="doctor-led fitness, clinical nutrition, doctor supervised weight loss, physician led fitness program, medical fitness coaching, doctor nutritionist, medically supervised diet, health coaching with doctor, preventive healthcare, lifestyle medicine" />
        <link rel="canonical" href="https://tcubefitness.com/doctor-led-fitness" />
        <meta property="og:title" content="Doctor-Led Fitness & Nutrition Programs | TCUBE FITNESS" />
        <meta property="og:description" content="Medical expertise meets fitness coaching. Personalized programs supervised by doctors for safe, effective health transformation." />
        <meta property="og:url" content="https://tcubefitness.com/doctor-led-fitness" />
        <meta property="og:type" content="website" />
      </Helmet>

      <SEOSchema
        type="service"
        name="Doctor-Led Fitness & Nutrition Program"
        description="Medical supervision, personalized clinical nutrition and evidence-based training for diabetes, thyroid, PMOS and metabolic health, fully online."
        url="/doctor-led-fitness"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Doctor-Led Fitness', path: '/doctor-led-fitness' },
        ]}
      />

      <div className="min-h-screen bg-slate-950">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-28 pb-20 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-600/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-6">
                <Stethoscope className="w-4 h-4" />
                Medical Expertise Meets Fitness
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Doctor-Led Fitness & Nutrition Programs
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                Experience the difference of medically supervised health transformation. Our MBBS-qualified doctors and certified nutritionists create personalized programs that prioritize your safety while delivering exceptional results.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                <Link
                  to="/submit-details"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-lg shadow-blue-500/25"
                  data-testid="doctor-led-cta"
                >
                  Start Your Medical Fitness Journey
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a 
                  href="https://wa.me/919515901777"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-slate-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-slate-800"
                >
                  Free Consultation
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-blue-400 font-bold text-lg">MBBS</p>
                  <p className="text-slate-500 text-sm">Qualified Doctors</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-emerald-400 font-bold text-lg">100%</p>
                  <p className="text-slate-500 text-sm">Online Programs</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-orange-400 font-bold text-lg">Weekly</p>
                  <p className="text-slate-500 text-sm">Medical Check-ins</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-pink-400 font-bold text-lg">Multi</p>
                  <p className="text-slate-500 text-sm">Specialist Team</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What is Doctor-Led Fitness Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 text-center">
                What is a <span className="text-blue-400">Doctor-Led</span> Nutrition Program?
              </h2>
              
              <div className="bg-slate-900/50 rounded-3xl p-8 border border-slate-800 mb-12">
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  A doctor-led nutrition program represents the gold standard in health and fitness coaching. Unlike conventional fitness programs that focus solely on exercise and generic diet plans, our approach integrates comprehensive medical expertise into every aspect of your transformation journey.
                </p>
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  At TCUBE FITNESS, our <Link to="/#team" className="text-blue-400 hover:underline">Medical Lead, Dr. Pooja Sree (MBBS, DNB General Medicine, Diploma in Sports Nutrition)</Link>, personally oversees each client's program. This means your health history, current medications, metabolic markers, and any existing conditions are carefully considered when designing your personalized plan.
                </p>
                <p className="text-slate-300 text-lg leading-relaxed">
                  Whether you are managing diabetes, dealing with thyroid issues, recovering from surgery, or simply want the assurance of medical supervision for your health goals, our doctor-led approach provides the safety, expertise, and personalization that generic fitness programs cannot offer.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Why Choose <span className="text-blue-400">Doctor-Led</span> Fitness?
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                The advantages of having medical professionals guide your health transformation
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {benefits.map((benefit, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800 hover:border-blue-500/30 transition-all"
                >
                  <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center text-blue-400 mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{benefit.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Conditions We Help Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Health Conditions We <span className="text-emerald-400">Support</span>
                </h2>
                <p className="text-slate-400">
                  Our medical team specializes in creating fitness and nutrition programs for various health conditions
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {conditions.map((condition, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-3 bg-slate-900/50 rounded-xl p-4 border border-slate-800"
                  >
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-slate-300">{condition}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 text-center">
                <p className="text-slate-400 mb-4">Have a different health concern?</p>
                <Link 
                  to="/submit-details"
                  className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium"
                >
                  Contact us for a personalized assessment
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Our <span className="text-orange-400">Medical Fitness</span> Process
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                A systematic approach to health transformation with medical oversight at every step
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <div className="relative">
                <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-blue-600 via-cyan-600 to-emerald-600 hidden md:block"></div>
                
                <div className="space-y-6">
                  {process.map((item, idx) => (
                    <div key={idx} className="flex gap-6 items-start">
                      <div className="relative z-10 w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0 shadow-lg">
                        {item.step}
                      </div>
                      <div className="bg-slate-900/50 rounded-2xl p-5 border border-slate-800 flex-1">
                        <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                        <p className="text-slate-400">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Doctor-Led Fitness <span className="text-blue-400">FAQs</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Common questions about our medically supervised programs
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-900/50 rounded-2xl border border-slate-800 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left"
                  >
                    <span className="text-white font-semibold pr-4">{faq.question}</span>
                    <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${openFaq === idx ? 'rotate-180 text-blue-400' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all ${openFaq === idx ? 'max-h-96' : 'max-h-0'}`}>
                    <p className="px-5 pb-5 text-slate-400 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/50 via-cyan-900/50 to-blue-900/50"></div>
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Ready for Medical-Grade Fitness Coaching?
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                Take the first step toward a safer, more effective health transformation with our doctor-led programs.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/submit-details"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-slate-100"
                >
                  Book Your Consultation
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/packages"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-white/10"
                >
                  View Programs
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Internal Links Section */}
        <section className="py-12 border-t border-slate-800">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-lg font-semibold text-white mb-4">Related Programs</h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/pregnancy-nutrition" className="text-slate-400 hover:text-emerald-400 transition-colors">Pregnancy Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/fertility-nutrition" className="text-slate-400 hover:text-pink-400 transition-colors">Fertility Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/medical-nutrition-therapy" className="text-slate-400 hover:text-blue-400 transition-colors">Clinical Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/online-fitness-coaching" className="text-slate-400 hover:text-orange-400 transition-colors">Online Fitness Coaching</Link>
                <span className="text-slate-700">|</span>
                <Link to="/packages" className="text-slate-400 hover:text-cyan-400 transition-colors">All Programs</Link>
              </div>
            </div>
          </div>
        </section>

        <ProgramFramework slug="doctor-led-fitness" />
        <Footer />
      </div>
    </>
  );
};

export default DoctorLedFitnessPage;
